import { easeExpInOut } from 'd3-ease';
// tslint:disable-next-line: no-submodule-imports
import useStyles from 'isomorphic-style-loader/useStyles';
import React, { useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { animated, useSpring } from 'react-spring';
import { isTransportOverlayDisabled } from '../utils/TransportOverlayHook';
import { HIDE_TRANSPORT_OVERLAY } from '../actions';
import { VISUAL_TABLE_WIDGET } from '../types/templates/widgets/ISkyfireVisualTableWidget';
import { dispatchPlaybackMethods } from '../utils/dispatchPlaybackMethods';
import * as Styles from './TransportOverlay.scss';
import Widget from './widgets';
import VisualTableWidget from './widgets/VisualTableWidget';
const headerStyles = {
    contain: 'layout',
    paddingTop: '20px',
    display: 'block',
    marginBottom: '0px',
};
export default function TransportOverlay() {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    useStyles(Styles);
    const dispatch = useDispatch();
    const { isTransportOverlayOpen, overlayTemplate, onDataRequired } = useSelector((state) => state.TransportOverlay, shallowEqual);
    const { windowHeight } = useSelector((state) => state.BrowserState, shallowEqual);
    const { mediaId } = useSelector((state) => state.Media);
    const isDisabled = isTransportOverlayDisabled();
    const emptyFunction = () => true;
    const hasLoaded = (_b = (_a = overlayTemplate) === null || _a === void 0 ? void 0 : _a.widgets) === null || _b === void 0 ? void 0 : _b.length;
    const hasNoUpcomingWidgets = ((_f = (_e = (_d = (_c = overlayTemplate) === null || _c === void 0 ? void 0 : _c.widgets) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.items) === null || _f === void 0 ? void 0 : _f.length) === 0;
    const trackCountClassName = [!hasLoaded && Styles.countLoading].join(' ');
    const durationClassName = [!hasLoaded && Styles.durationLoading].join(' ');
    const TransportOverlayAnimationValues = useSpring({
        opacity: isTransportOverlayOpen ? 1 : 0,
        height: isTransportOverlayOpen ? windowHeight - 80 : 0,
        config: { easing: easeExpInOut },
    });
    let timeoutHandle;
    // Check if Transport Overlay is open, disable root scroller to use Transport Overlay scroller.
    useEffect(() => {
        var _a;
        const windowScrollElement = document.documentElement;
        if (isTransportOverlayOpen) {
            (_a = timeoutHandle) === null || _a === void 0 ? void 0 : _a.clear();
            windowScrollElement.style['overflow-y'] = 'hidden';
        }
        else {
            timeoutHandle = setTimeout(() => {
                windowScrollElement.style['overflow-y'] = 'scroll';
            }, 500);
        }
    }, [isTransportOverlayOpen]);
    // If there's no data, fetch some. We need to fetch data, even if the Transport Overlay isn't open for
    // the time being to show the disabled state for the Transport Overlay Button until UX has a better
    // solution.
    useEffect(() => {
        if (!overlayTemplate && isTransportOverlayOpen) {
            dispatchPlaybackMethods(dispatch, mediaId, onDataRequired);
        }
    }, [mediaId, isTransportOverlayOpen]);
    // Handle Escape key shortcut.
    useEffect(() => {
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, []);
    // If there's no upcoming widgets, hide the Transport Overlay.
    useEffect(() => {
        if (hasNoUpcomingWidgets && hasLoaded) {
            dispatch({ type: HIDE_TRANSPORT_OVERLAY });
        }
    }, [hasNoUpcomingWidgets]);
    useEffect(() => {
        if (isTransportOverlayOpen && isDisabled) {
            dispatch({ type: HIDE_TRANSPORT_OVERLAY });
        }
    }, [isDisabled]);
    const widgets = (_g = overlayTemplate) === null || _g === void 0 ? void 0 : _g.widgets;
    return (React.createElement(animated.div, { style: TransportOverlayAnimationValues, className: Styles.transportOverlay, id: "transport-overlay" },
        React.createElement("music-detail-header", { style: headerStyles, headline: (_h = overlayTemplate) === null || _h === void 0 ? void 0 : _h.title, className: hasLoaded ? '' : Styles.hidden }),
        React.createElement("header", { className: hasLoaded ? Styles.hidden : Styles.headerLoading }),
        React.createElement("b", { className: Styles.subtitle },
            ((_j = overlayTemplate) === null || _j === void 0 ? void 0 : _j.subTitle) &&
                React.createElement("span", { className: Styles.subtitleText }, (_k = overlayTemplate) === null || _k === void 0 ? void 0 : _k.subTitle),
            ((_l = overlayTemplate) === null || _l === void 0 ? void 0 : _l.trackCount) && React.createElement("span", { className: trackCountClassName }, (_m = overlayTemplate) === null || _m === void 0 ? void 0 : _m.trackCount),
            isTransportOverlayOpen && ((_o = overlayTemplate) === null || _o === void 0 ? void 0 : _o.totalDuration) ? React.createElement("span", null, "\u2022") : null,
            isTransportOverlayOpen && React.createElement("span", { className: durationClassName }, convertToReadableTime((_p = overlayTemplate) === null || _p === void 0 ? void 0 : _p.totalDuration))),
        React.createElement("div", { className: hasLoaded ? '' : Styles.hidden }, renderWidgets(widgets)),
        React.createElement("div", { className: hasLoaded ? Styles.hidden : '' },
            React.createElement(VisualTableWidget, { data: {
                    interface: VISUAL_TABLE_WIDGET,
                    items: Array(20).fill(1),
                    onEndOfWidget: [],
                    onTrackReorder: [],
                    onViewed: []
                }, handleSelected: emptyFunction, loading: true }))));
    function handleEscape(e) {
        if (e.keyCode === 27) {
            dispatch({ type: HIDE_TRANSPORT_OVERLAY });
        }
    }
    function convertToReadableTime(durationString) {
        if (!durationString) {
            return undefined;
        }
        const minutes = Number(durationString.substr(0, durationString.indexOf(':')));
        if (minutes) {
            return String(Math.floor(minutes / 60)) + 'hr ' + String(minutes % 60) + ' min';
        }
        return undefined;
    }
    function renderWidgets(widgetsToRender) {
        var _a;
        return (_a = widgetsToRender) === null || _a === void 0 ? void 0 : _a.map((widget) => {
            if (widget.interface === VISUAL_TABLE_WIDGET) {
                return (React.createElement(VisualTableWidget, { data: widget, handleSelected: handleSelected, isVisualPlayQueue: true }));
            }
            return React.createElement(Widget, { data: widget, handleSelected: handleSelected });
        });
    }
    function handleSelected(onItemSelected) {
        var _a;
        if (!((_a = onItemSelected) === null || _a === void 0 ? void 0 : _a.length)) {
            return;
        }
        dispatchPlaybackMethods(dispatch, mediaId, onItemSelected);
        dispatch({ type: HIDE_TRANSPORT_OVERLAY });
        dispatchPlaybackMethods(dispatch, mediaId, onDataRequired);
    }
}
